import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, catchError, BehaviorSubject } from 'rxjs';
import { SupplierOrders } from 'src/app/models/supplier-portal/supplier/supplierOrders.models';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from '../shared/error-handler.service';
import { DTO } from 'src/app/models/supplier-portal/supplier/supplierDTOS.models';
import { STORAGE_KEYS } from 'src/app/constants/storage-keys';
import { IWsResult } from 'src/app/models/ws_result.model';

@Injectable({
  providedIn: 'root',
})
export class OrderSupplierService {
  public orderNumberCounter: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);

  private readonly serviceBase = environment.urlServer;
  private readonly orderSupplierEndoints = {
    getOpenCertifications: (supplierId: number) =>
      `${this.serviceBase}/get_open_certifications/${supplierId}/`,
    getClosedCertifications: (supplierId: number) =>
      `${this.serviceBase}/get_close_certifications/${supplierId}/`,
    getPartialCertifications: (supplierId: number) =>
      `${this.serviceBase}/get_partial_certifications/${supplierId}/`,
    getNonConformingCertifications: (supplierId: number) =>
      `${this.serviceBase}/get_non_conforming_certifications/${supplierId}/`,
    createCertification: `${this.serviceBase}/put_new_certification/`,
    deleteCertification: `${this.serviceBase}/del_certification/`,
    searchCertifications: `${this.serviceBase}/search_certifications/`,
    getSupplier: (supplierId: number) =>
      `${this.serviceBase}/get_supplier/${supplierId}/`,
    addNewOrder: `${this.serviceBase}/put_new_certification/`,
  } as const;

  public constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerServ: ErrorHandlerService,
  ) {}

  get suplierDetails() {
    return JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SUPPLIER_DETAILS) || '{}',
    );
  }

  setSuplierDetails(supplier: SupplierOrders.Supplier) {
    localStorage.setItem(
      STORAGE_KEYS.SUPPLIER_DETAILS,
      JSON.stringify(supplier),
    );
  }

  public getSupplier(supplierId: number): Observable<SupplierOrders.Supplier> {
    return this.http
      .get<DTO.DefaultResponseDTO>(
        this.orderSupplierEndoints.getSupplier(supplierId),
      )
      .pipe(
        map((response) => {
          const supplier = new SupplierOrders.Supplier(response.data);
          this.setSuplierDetails(supplier);
          return supplier;
        }),
        catchError(this.errorHandlerServ.handleError<any>('getSupplier')),
      );
  }

  public getPartialCertifications(
    payload: number,
  ): Observable<SupplierOrders.OrdersResponsePartial> {
    return this.http
      .get<SupplierOrders.OrdersResponsePartial>(
        this.orderSupplierEndoints.getPartialCertifications(payload),
      )
      .pipe(
        map((response) => {
          return new SupplierOrders.OrdersResponsePartial(response);
        }),
        catchError(
          this.errorHandlerServ.handleError<SupplierOrders.OrdersResponsePartial>(
            'getPartialCertifications',
          ),
        ),
      );
  }

  public addNewOrder(
    payload: SupplierOrders.IAddNewOrderFromGamma,
  ): Observable<IWsResult> {
    return this.http
      .post<IWsResult>(this.orderSupplierEndoints.addNewOrder, payload)
      .pipe(
        map((response) => {
          // const responseFromGamma = new SupplierOrders.ResponseOrderFromGamma(
          //   response.data,
          // );
          return response;
        }),
        catchError(
          this.errorHandlerServ.handleError<SupplierOrders.ResponseOrderFromGamma>(
            'addNewOrder',
          ),
        ),
      );
  }

  public getOpenCertifications(
    supplierId: number,
  ): Observable<SupplierOrders.OrdersResponse> {
    return this.http
      .get<SupplierOrders.OrdersResponse>(
        this.orderSupplierEndoints.getOpenCertifications(supplierId),
      )
      .pipe(
        map((response) => {
          const openCertifications = new SupplierOrders.OrdersResponse(
            response,
          );
          return openCertifications;
        }),
        catchError(
          this.errorHandlerServ.handleError<SupplierOrders.OrdersResponse>(
            'getOpenCertifications',
          ),
        ),
      );
  }

  public getClosedCertifications(
    supplierId: number,
  ): Observable<SupplierOrders.OrdersResponse> {
    return this.http
      .get<SupplierOrders.OrdersResponse>(
        this.orderSupplierEndoints.getClosedCertifications(supplierId),
      )
      .pipe(
        map((response) => {
          return new SupplierOrders.OrdersResponse(response);
        }),
        catchError(
          this.errorHandlerServ.handleError<SupplierOrders.OrdersResponse>(
            'getClosedCertifications',
          ),
        ),
      );
  }

  public getNonConformingCertifications(
    supplierId: number,
  ): Observable<SupplierOrders.OrdersResponse> {
    return this.http
      .get<SupplierOrders.OrdersResponse>(
        this.orderSupplierEndoints.getNonConformingCertifications(supplierId),
      )
      .pipe(
        map((response) => {
          return new SupplierOrders.OrdersResponse(response);
        }),
        catchError(
          this.errorHandlerServ.handleError<SupplierOrders.OrdersResponse>(
            'getNonConformingCertifications',
          ),
        ),
      );
  }

  // public createCertification(
  //   payload: SupplierPortalNewCertificationRequest,
  // ): Observable<SupplierPortalNewCertificationResponse> {
  //   return this.http
  //     .post<SupplierPortalNewCertificationResponse>(
  //       this.orderSupplierEndoints.createCertification,
  //       payload,
  //     )
  //     .pipe(
  //       catchError(
  //         this.errorHandlerServ.handleError<SupplierPortalNewCertificationResponse>(
  //           'createCertification',
  //         ),
  //       ),
  //     );
  // }

  public deleteCertification(id_certification: number) {
    return this.http
      .post(
        `${this.orderSupplierEndoints.deleteCertification}${id_certification}`,
        {},
      )
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<unknown>('deleteCertificationDDT'),
        ),
      );
  }

  //   public deleteCertification(id_certification: number) {
  //   return this.http
  //     .delete(
  //       `${this.orderSupplierEndoints.deleteCertification}${id_certification}`
  //     )
  //     .pipe(
  //       catchError(
  //         this.errorHandlerServ.handleError<unknown>('deleteCertificationDDT'),
  //       ),
  //     );
  // }

  // public searchCertifications(
  //   payload: SupplierPortalSearchCertificationsRequest,
  // ): Observable<SupplierPortalSearchResultsResponse> {
  //   const params = new HttpParams()
  //     .set('q', payload.q)
  //     .set('supplier_id', payload.supplier_id);

  //   return this.http
  //     .get<SupplierPortalSearchResultsResponse>(
  //       this.orderSupplierEndoints.searchCertifications,
  //       { params },
  //     )
  //     .pipe(
  //       catchError(
  //         this.errorHandlerServ.handleError<SupplierPortalSearchResultsResponse>(
  //           'searchCertifications',
  //         ),
  //       ),
  //     );
  // }
}
